import { useMatches } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE, isLocale, type SupportedLocale } from "@translations/config";

/**
 * Resolve the active locale from the route match params.
 *
 * Routes under the `/$lang` layout receive a `lang` param ("fr" today).
 * Routes mounted directly under the root have no `lang` param and fall
 * back to the default locale (English).
 */
export function useLocale(): SupportedLocale {
  const matches = useMatches();
  for (const m of matches) {
    const params = m.params as { lang?: string } | undefined;
    if (params?.lang && isLocale(params.lang)) {
      return params.lang;
    }
  }
  return DEFAULT_LOCALE;
}

/**
 * Sync i18next's active language with the locale derived from the URL.
 * Mount this inside the locale-aware layout(s) so language follows navigation.
 */
export function useSyncI18nLocale(locale: SupportedLocale): void {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [i18n, locale]);
}
